import * as React from "react";
import { useEffect, useState } from "react";
import AddEmailActivity from "./AddEmailActivity";
import AddNoteActivity from "./AddNoteActivity";
import { ActivityTimelineActions } from "../patient-management/ActivityTimelineActions";
import InvitePatientAction from "./InvitePatientAction";
import { PatientManagementInfo } from "../commons/model/patient/PatientInfo";
import TextAction from "../commons/components/action/TextAction";

export default function ActivityActionsPanel({
  actions,
  patient,
}: {
  actions: ActivityTimelineActions;
  patient: PatientManagementInfo;
}) {
  const [activeAction, setActiveAction] = useState<ActivityActionType>(
    ActivityActionType[window.location.hash.substring(1)]
  );

  useEffect(() => {
    if (activeAction) {
      window.onbeforeunload = () => "prevent-data-lose";
    } else {
      window.onbeforeunload = () => {};
    }
  }, [activeAction]);
  useEffect(() => (window.onbeforeunload = () => {}), []);

  return (
    <>
      <Actions
        actions={actions}
        activeAction={activeAction}
        setActiveAction={setActiveAction}
        close={() => setActiveAction(undefined)}
        patient={patient}
      />
      {actions.inviteAction && (
        <InvitePatientAction patient={patient} action={actions.inviteAction} />
      )}
    </>
  );
}

const Actions = ({
  actions,
  activeAction,
  setActiveAction,
  close,
  patient,
}: {
  actions: ActivityTimelineActions;
  activeAction: ActivityActionType;
  setActiveAction: React.Dispatch<React.SetStateAction<ActivityActionType>>;
  close: () => any;
  patient: PatientManagementInfo;
}) => {
  const actionTypes = [];
  if (actions.emailAction) {
    actionTypes.push(ActivityActionType.EMAIL);
  }
  if (actions.noteAction) {
    actionTypes.push(ActivityActionType.NOTE);
  }

  const hasGuardianWithEmail = (patient: PatientManagementInfo) =>
    patient.legalGuardians &&
    patient.legalGuardians.length &&
    patient.legalGuardians.find((guardian) => guardian.email);

  const shouldDisableAction = (action: ActivityActionType, patient: PatientManagementInfo) =>
    action === ActivityActionType.EMAIL &&
    !patient.patientInformation.email &&
    !hasGuardianWithEmail(patient);

  if (actionTypes.length) {
    return (
      <div className="activity">
        <div className="timeline">
          <div className="line" />
        </div>
        <div className="flex-auto">
          <div className="item actions h-flex with-separator">
            {actionTypes.map((action) => (
              <React.Fragment key={action.toLocaleLowerCase()}>
                <TextAction
                  id={"new-" + action.toLocaleLowerCase()}
                  icon={<i className={iconForActivityAction[action]} />}
                  disabled={shouldDisableAction(action, patient)}
                  label={i18n[`Activity.Action.${action.toString()}.Title`]}
                  onClick={() => setActiveAction(action)}
                />
              </React.Fragment>
            ))}
          </div>
          <ActivityActionComponent activeAction={activeAction} actions={actions} close={close} />
        </div>
      </div>
    );
  } else {
    return null;
  }
};

const ActivityActionComponent = ({
  activeAction,
  actions,
  close,
}: {
  activeAction: ActivityActionType;
  actions: ActivityTimelineActions;
  close: () => any;
}) => {
  let component: React.JSX.Element;
  switch (activeAction) {
    case ActivityActionType.NOTE:
      component = <AddNoteActivity action={actions.noteAction} close={close} />;
      break;
    case ActivityActionType.EMAIL:
      component = <AddEmailActivity action={actions.emailAction} close={close} />;
      break;
  }
  return component ? (
    <div className="panel with-shadow">
      <div className="new-activity-dialog">
        <div className="dialog-title">{i18n[`Activity.Action.${activeAction}.Title`]}</div>
        {component}
      </div>
    </div>
  ) : null;
};

export enum ActivityActionType {
  NOTE = "Note",
  EMAIL = "Email",
}

const iconForActivityAction: { [key in ActivityActionType]: string } = {
  [ActivityActionType.NOTE]: "ic_edit",
  [ActivityActionType.EMAIL]: "ic_mail",
};
